import React, { useState, useRef, useEffect } from "react";
import { Info } from "@phosphor-icons/react";

/**
 * Lightweight, dependency-free info tooltip. Click-to-open on touch devices,
 * hover on desktop. Positioned above the icon with an arrow, auto-flips
 * downward if it would overflow the top of the viewport.
 *
 * Usage:
 *   <InfoTooltip label="What's Runway?">
 *     Runway is the number of months your current cash pile will last…
 *     <a href="/help/runway">Learn more →</a>
 *   </InfoTooltip>
 */
export default function InfoTooltip({ label, children, size = 14 }) {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState("top"); // 'top' | 'bottom'
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEsc);
    // Flip up/down based on trigger position.
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      if (rect.top < 200) setPlacement("bottom");
      else setPlacement("top");
    }
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open]);

  return (
    <span
      ref={ref}
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label={label ? `Help: ${label}` : "Help"}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="inline-flex items-center justify-center text-black/50 hover:text-black transition-colors focus:outline-none"
        data-testid="info-tooltip-trigger"
      >
        <Info size={size} weight="duotone" />
      </button>
      {open && (
        <span
          role="tooltip"
          className={`absolute z-50 w-72 max-w-[80vw] p-3 bg-white text-black text-xs leading-relaxed rounded-xl border-[1.5px] border-black shadow-[4px_4px_0_#000] left-1/2 -translate-x-1/2 ${
            placement === "top" ? "bottom-full mb-2" : "top-full mt-2"
          }`}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          data-testid="info-tooltip-body"
        >
          {label && (
            <span className="block font-heading uppercase tracking-widest text-[10px] mb-1">{label}</span>
          )}
          <span className="block">{children}</span>
        </span>
      )}
    </span>
  );
}
